import React from 'react';
import { X, ShoppingCart } from 'lucide-react';
import { useCart } from '../contexts/CartContext';
import '../styles/CartDrawer.css';

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
}

const CartDrawer: React.FC<CartDrawerProps> = ({ isOpen, onClose }) => {
  const { cartItems, removeFromCart, updateQuantity, getCartTotal } = useCart();

  return (
    <>
      {/* Overlay */}
      {isOpen && <div className="cart-overlay" onClick={onClose} />}

      {/* Drawer */}
      <div className={`cart-drawer ${isOpen ? 'cart-drawer-open' : ''}`}>
        {/* Header */}
        <div className="cart-header">
          <h2 className="cart-title">Bag</h2>
          <button onClick={onClose} className="cart-close-button">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="cart-content">
          {cartItems.length === 0 ? (
            <div className="cart-empty">
              <div className="cart-empty-content">
                <div className="cart-empty-icon">
                  <ShoppingCart size={64} strokeWidth={1.5} />
                </div>
                <h3 className="cart-empty-title">Your bag is empty</h3>
                <p className="cart-empty-text">Add some items to get started</p>
                <button onClick={onClose} className="cart-empty-button">
                  Continue Shopping
                </button>
              </div>
            </div>
          ) : (
            <div className="cart-layout">
              {/* Left Side - Cart Items */}
              <div className="cart-items-section">
                <div className="cart-items">
                  {cartItems.map((item) => (
                    <div key={item.id} className="cart-item">
                      <img src={item.image} alt={item.name} className="cart-item-image" />
                      <div className="cart-item-details">
                        <h3 className="cart-item-name">{item.name}</h3>
                        <p className="cart-item-price">{item.price}</p>
                        <div className="cart-item-quantity">
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity - 1)}
                            className="quantity-button"
                          >
                            -
                          </button>
                          <span className="quantity-value">{item.quantity}</span>
                          <button
                            onClick={() => updateQuantity(item.id, item.quantity + 1)}
                            className="quantity-button"
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="cart-item-remove"
                      >
                        <X size={20} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Side - Subtotal Card */}
              <div className="cart-summary-section">
                <div className="cart-summary-card">
                  <h3 className="cart-summary-title">Summary</h3>
                  <div className="cart-summary-line">
                    <span className="cart-summary-label">Subtotal</span>
                    <span className="cart-summary-value">${getCartTotal().toFixed(2)}</span>
                  </div>
                  <div className="cart-summary-line">
                    <span className="cart-summary-label">Shipping</span>
                    <span className="cart-summary-value">Free</span>
                  </div>
                  <div className="cart-summary-divider"></div>
                  <div className="cart-summary-total">
                    <span className="cart-total-label">Total</span>
                    <span className="cart-total-amount">${getCartTotal().toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="cart-footer">
          <button onClick={onClose} className="continue-shopping-button">
            Continue Shopping
          </button>
          {cartItems.length > 0 && (
            <button className="checkout-button">
              Checkout
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default CartDrawer;
